import React, { useEffect, useState } from "react";
import { withRouter } from "react-router-dom";
import FileDownload from "js-file-download";
import { toast } from "react-toastify";
import useStore from "../../../context/useStore";
import { financierActions } from "../../../context/actions/financierActions";
import useEncoursList from "./hooks/useEncoursList";
import financeV2Adapters from "../../../adapters/financeV2Adapters";
import financeApiService from "../../../services/financeApiService";
import tools from "../../../helpers/tools";
import Loader from "../../common/Loader";
import CustomPagination from "../../common/CustomPagination";
import styles from "./FinanceListV2.module.scss";

// LS-623 — encours Crédit-Bail : une ligne par véhicule financé des demandes
// Transmis et signé (paliers, mensualité courante, montant à échoir ventilé
// par véhicule, suivi de solde LS-722). V1 volontairement basique, proposée
// en attendant le tableau cible de Leasecar (visio du 10/09/2026). Montants
// HT, comme l'échéancier.

const STATUT_SOLDE_LABELS = {
  demande_envoyee: "Demande envoyée",
  facture_recue: "Facture reçue",
  solde: "Soldé",
};

const formatEuro = (n) =>
  n != null ? `${Number(n).toLocaleString("fr-FR")} €` : "—";

const EncoursCBList = (props) => {
  const [state, dispatch] = useStore();
  const actions = financierActions(state, dispatch, props);
  const financiers = state.financier.items;
  const [isExporting, setIsExporting] = useState(false);

  const {
    filters,
    updateFilter,
    resetFilters,
    setPage,
    encours,
    meta,
    isLoading,
  } = useEncoursList();

  useEffect(() => {
    actions.getAllFinanciers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Export xlsx, mêmes filtres que ceux appliqués à l'écran (sans pagination).
  async function handleExport() {
    setIsExporting(true);
    try {
      const query = tools.getSearchString(
        financeV2Adapters.toApi.filtresEncours(filters),
      );
      const res = await financeApiService.exporterEncours(query);
      FileDownload(res.data, "encours-credit-bail.xlsx");
    } catch {
      toast.error("L'export de l'encours a échoué.");
    } finally {
      setIsExporting(false);
    }
  }

  return (
    <div className={styles.financeList}>
      <div className={styles.header}>
        <h1>Encours CB</h1>
        <div className={styles.headerActions}>
          <button
            className={styles.btnExport}
            onClick={handleExport}
            disabled={isExporting}
          >
            <i
              className={`fa fa-${isExporting ? "spinner fa-spin" : "download"}`}
              style={{ marginRight: 6 }}
            />
            {isExporting ? "Export..." : "Exporter"}
          </button>
        </div>
      </div>

      <div className={styles.filterBar}>
        <select
          className={styles.filterSelect}
          value={filters.financeurUuid}
          onChange={(e) => updateFilter("financeurUuid", e.target.value)}
        >
          <option value="">Tous les financeurs</option>
          {financiers.map((f) => (
            <option key={f.uuid} value={f.uuid}>
              {f.name}
            </option>
          ))}
        </select>
        <input
          className={styles.filterInput}
          placeholder="Immatriculation…"
          value={filters.immatriculation}
          onChange={(e) => updateFilter("immatriculation", e.target.value)}
        />
        {/* Soldé = solde payé ; un solde seulement demandé reste un encours. */}
        <select
          className={styles.filterSelect}
          value={filters.solde}
          onChange={(e) => updateFilter("solde", e.target.value)}
        >
          <option value="">Soldés et non soldés</option>
          <option value="0">Non soldés</option>
          <option value="1">Soldés</option>
        </select>
        {/* LS-743 — vendu = véhicule de flotte passé à un statut 6x (facturé
            ou non) ; non soldé = solde non payé. À solder auprès du financeur. */}
        <label className={styles.filterCheck}>
          <input
            type="checkbox"
            checked={filters.venduNonSolde === "1"}
            onChange={(e) =>
              updateFilter("venduNonSolde", e.target.checked ? "1" : "")
            }
          />
          Vendus non soldés
        </label>
        <button
          className={styles.filterReset}
          onClick={resetFilters}
          title="Réinitialiser les filtres"
        >
          <i className="fa fa-times" />
        </button>
      </div>

      {isLoading ? (
        <Loader />
      ) : (
        <>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Immatriculation</th>
                <th>Financeur</th>
                <th>N° contrat</th>
                <th>Compte comptable</th>
                <th>Dossier</th>
                <th>Paliers (HT)</th>
                <th>Mensualité courante HT</th>
                <th>À échoir / véhicule HT</th>
                <th>Solde</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {encours.length === 0 && (
                <tr>
                  <td colSpan={10} className={styles.empty}>
                    Aucun encours trouvé.
                  </td>
                </tr>
              )}
              {encours.map((ligne) => (
                <tr key={`${ligne.demandeUuid}-${ligne.devisUuid}-${ligne.immatriculation}`}>
                  <td className={styles.cellRef}>
                    {ligne.immatriculation}
                    {ligne.venduNonSolde && (
                      <span
                        className={styles.badgeVenduNonSolde}
                        title="Véhicule vendu (statut flotte 6x) dont le solde n'est pas payé : demande de solde à faire au financeur"
                      >
                        <i className="fas fa-exclamation-triangle" /> Vendu non
                        soldé
                      </span>
                    )}
                  </td>
                  <td>{ligne.financeur.nom}</td>
                  <td>{ligne.numeroContratFinanceur || "—"}</td>
                  {/* LS-717 — compte du dossier, identique sur tous ses véhicules. */}
                  <td>{ligne.compteComptable || "—"}</td>
                  <td className={styles.cellDevis}>
                    {ligne.reference}
                    {ligne.devisReference ? ` · ${ligne.devisReference}` : ""}
                  </td>
                  <td className={styles.cellDevis}>
                    {ligne.paliers.map((p) => (
                      <div key={p.numero}>
                        P{p.numero} :{" "}
                        {p.mensualiteRetenue != null
                          ? formatEuro(p.mensualiteRetenue)
                          : "à saisir"}
                        {p.dateDebut
                          ? ` (${tools.formatDate(p.dateDebut)}${
                              p.dateFin
                                ? ` → ${tools.formatDate(p.dateFin)}`
                                : ""
                            })`
                          : ""}
                      </div>
                    ))}
                  </td>
                  <td>{formatEuro(ligne.mensualiteCourante)}</td>
                  {/* À-échoir du devis ÷ nb de véhicules du devis (règle actée
                      10/09) ; le montant devis complet est rappelé au survol. */}
                  <td
                    title={
                      ligne.montantAEchoirDevis != null
                        ? `Devis complet : ${formatEuro(ligne.montantAEchoirDevis)} pour ${ligne.nbVehiculesDevis} véhicule(s)`
                        : "Échéancier incomplet : montant indisponible"
                    }
                  >
                    {formatEuro(ligne.montantAEchoirVehicule)}
                  </td>
                  <td>
                    {ligne.solde ? (
                      <span
                        className={styles.badge}
                        title={[
                          ligne.solde.dateDemande &&
                            `Demandé le ${tools.formatDate(ligne.solde.dateDemande)}`,
                          ligne.solde.montant != null &&
                            `Montant ${formatEuro(ligne.solde.montant)}`,
                          ligne.solde.datePaiement &&
                            `Payé le ${tools.formatDate(ligne.solde.datePaiement)}`,
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      >
                        {STATUT_SOLDE_LABELS[ligne.solde.statut] ||
                          ligne.solde.statut}
                      </span>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td>
                    <button
                      className={styles.btnOpen}
                      onClick={() =>
                        props.history.push("/financements/" + ligne.demandeUuid)
                      }
                    >
                      Ouvrir
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <CustomPagination
            total={meta.total}
            activePage={meta.current_page}
            handlePageParamsChange={({ page }) => setPage(page)}
          />
        </>
      )}
    </div>
  );
};

export default withRouter(EncoursCBList);
